import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Paginator } from "primereact/paginator";
import { ProgressSpinner } from "primereact/progressspinner";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import DatasetTableDefault from "components/dashboard/dataset-table/DatasetTableDefault";
import DatasetTableGrouped from "components/dashboard/dataset-table/DatasetTableGrouped";
import DatasetTableTransposed from "components/dashboard/dataset-table/DatasetTableTransposed";
import { useComponentStateCallback } from "hooks/dashboard/useComponentStateCallback";
import useSafeId from "hooks/dashboard/useDashboardComponentId";
import * as XLSX from "xlsx-js-style";
import { useGetDataInfoRange } from "api/api-hooks/datasets-hooks";
import { downloadBlobAsCsvFile } from "utils/file-utils";
import { JodaTimeUtils } from "utils/joda-time-utils";
import {
  addToDate,
  ChronoUnitLegacy,
  dateBody,
  formatTime,
  formatToIsoStringWithoutMillis,
  truncateDate,
} from "utils/time-utils";
import { findValue, timeConfigValidation } from "utils/utils";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {
  DatasetTableMode,
  IDatasetTotal,
  IFlattenedDataset,
} from "components/dashboard/dataset-table/utils/interfaces";
import "components/dashboard/dataset-table/DatasetTable.scss";
import {
  DatasetDefaultConfig,
  DatasetFullDefaultConfig,
  HighlightMapped,
} from "models/dataset-models";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { getValueColors } from "../product-template-table/utils/utils";
import { IDataSet } from "../charts/interfaces";
import { getRowData } from "./utils/utils";

const DatasetTable = (props: DatasetFullDefaultConfig) => {
  const { datasets, config, reportOptions, referenceTime, reportMode } = props;
  const tableMode = props.mode || DatasetTableMode.SINGLE;
  const uniqueComponentId = useSafeId();
  const tableId = useSafeId();
  const setStatus = useComponentStateCallback(props);

  const identifiers = React.useRef<Array<string>>([]);
  const dateFormat = config?.dateFormat || ChronoUnitLegacy.MINUTES;

  const [refresh, setRefresh] = useState<boolean | undefined>(undefined);
  const [frequency, setFrequency] = useState<number>(
    config?.startAutoRefreshFrequency && config.startAutoRefreshFrequency > 0
      ? config.startAutoRefreshFrequency * 1000
      : 0
  );

  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();

  const [requesting, setRequesting] = useState<boolean>(false);

  const [allSteps, setAllSteps] = useState<Array<Date>>();
  const [filteredSteps, setFilteredSteps] = useState<number>();

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rows, setRows] = useState(config?.itemsPerPage || 10);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [totals, setTotals] = useState<IDatasetTotal[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formatNumbers, setFormatNumbers] = useState<boolean>(true);

  const {
    data: dataset,
    isLoading,
    refetch,
    error,
  } = useGetDataInfoRange(identifiers.current ? identifiers.current : [], {
    timeStart: dateFrom?.toISOString() ?? new Date().toISOString(),
    timeEnd: dateTo?.toISOString(),
  });

  useEffect(() => {
    if (error) {
      easasAlert({ summary: "Failed to fetch data", detail: error.message, severity: "error" });
      setStatus({ state: "error", error: error.message });
    }
  }, [error]);

  const rowsPerPageOptions = config?.itemsPerPage
    ? [config.itemsPerPage, 10, 20, 30]
        .sort((a, b) => a - b)
        .filter(function (x, i, a) {
          return a.indexOf(x) === i;
        })
    : [10, 20, 30];

  const mappedHighlights: Array<HighlightMapped> = datasets
    ? datasets
        .map(ob => ({
          identifier: ob.identifier,
          highlightRanges: ob.highlightRanges,
          defaultHighlight: ob.defaultHighlight,
        }))
        .filter(e => e.defaultHighlight || (e.highlightRanges && e.highlightRanges.length > 0))
    : [];

  useEffect(() => {
    const tempIdentifiers: string[] = [];

    const extractIdentifiers = (obj: any) => {
      if (obj.identifier) {
        tempIdentifiers.push(obj.identifier);
      }
      if (obj.children) {
        obj.children.forEach((child: any) => extractIdentifiers(child));
      }
    };

    datasets?.forEach(ds => extractIdentifiers(ds));

    if (tempIdentifiers.length > 0) {
      const uniqueIdentifiers = new Set(tempIdentifiers);
      if (uniqueIdentifiers.size !== tempIdentifiers.length) {
        const duplicates = tempIdentifiers.filter(
          (item, index) => tempIdentifiers.indexOf(item) !== index
        );
        setErrorMessage(`Error: Duplicate datasets: ${duplicates.join(", ")}.`);
        return;
      }
      identifiers.current = Array.from(uniqueIdentifiers);
    }
    truncateStarterDateFromConfig();
    setRefresh(true);
  }, []);

  useEffect(() => {
    if (!isLoading) {
      const minTableWidthElement = document.getElementsByClassName("min-table-width")[0];
      if (!config?.transposed && minTableWidthElement) {
        minTableWidthElement.style.minWidth = `${identifiers.current.length * 100 + 100}px`;
      }

      if (reportOptions && reportMode) {
        const table = document.querySelector(`#${tableId}`);
        if (reportOptions.fontSize && table) {
          table.style.fontSize = reportOptions.fontSize;
        }
      }
      setStatus({ state: "finished" });
    }
  }, [isLoading]);

  useEffect(() => {
    getStepsCount();
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (refresh) {
      if (config?.startAutoRefreshFrequency && !reportMode) {
        setFrequency(
          config.startAutoRefreshFrequency > 0 ? config.startAutoRefreshFrequency * 1000 : 0
        );
      }
      truncateStarterDateFromConfig();
    }
  }, [refresh]);

  useEffect(() => {
    if (refresh && frequency > 0) {
      const interval = setInterval(() => {
        truncateStarterDateFromConfig();
        refetch();
      }, frequency);
      return () => clearInterval(interval);
    }
    return () => {};
  }, [refresh, frequency]);

  useEffect(() => {
    if (isDialogOpen) {
      setFrequency(0);
    }
  }, [isDialogOpen]);

  const mapDataTransposed = (currentData: Array<DatasetDataInfoResponse>, pageNumber?: number) => {
    const dates = getPageDates(pageNumber || currentPage);
    return mapAndTransformDataResponse(dates, currentData);
  };

  const updateDatasetsArrayWithInfo = (dataArray: Array<DatasetDataInfoResponse>): Array<any> => {
    if (!dataArray) {
      return [];
    }
    const updateObject = (obj: IDataSet & HighlightMapped): IDataSet & HighlightMapped => {
      if (obj.identifier) {
        const found = dataArray.find(a => a.identifier === obj.identifier);
        if (found) {
          return {
            ...obj,
            data: found.data,
            title: found.title,
            unit: found.unit,
            stringValueMapper: found.stringValueMapper,
            defaultHighlight: obj.defaultHighlight,
            highlightRanges: obj.highlightRanges,
            decimalPlaces: obj.decimalPlaces != null ? obj.decimalPlaces : found.decimalPlaces ?? 2,
          };
        }
      }
      if (obj.children) {
        const updatedChildren = obj.children.map((child: IDataSet & HighlightMapped) =>
          updateObject(child)
        );
        return { ...obj, children: updatedChildren };
      }
      return obj;
    };

    return dataArray.map(updateObject);
  };

  const mapData = (arrayData: Array<DatasetDataInfoResponse>) => {
    const dates = getAllDates(arrayData);
    return mapAndReplaceDateProperties(dates, arrayData);
  };

  const getStepsCount = () => {
    if (dateFrom && dateTo) {
      const dateRange = compareTwoDates(dateTo, dateFrom);
      let { from } = dateRange;
      const { to } = dateRange;
      const stepSize: number = (config && config.stepSize) || 1;
      const stepUnits: ChronoUnitLegacy = config?.stepUnits ?? ChronoUnitLegacy.HOURS;
      const stepsArr: Array<Date> = [];
      const convertedUnit = JodaTimeUtils.convertLegacyChronoUnitToJoda(stepUnits);
      from = JodaTimeUtils.CeilingTo(from, convertedUnit);
      while (from <= to) {
        stepsArr.push(from);
        from = addToDate(from, stepSize, stepUnits);
      }
      setAllSteps([...(config?.sort === "ASC" ? stepsArr : stepsArr.reverse())]);
    }
  };

  const compareTwoDates = (first: Date, second: Date) => {
    if (first > second) {
      return { from: second, to: first };
    }
    return { from: first, to: second };
  };

  const truncateStarterDateFromConfig = () => {
    const { from, to } = getTimeRange();
    if (
      new Date(from).getTime() !== dateFrom?.getTime() ||
      new Date(to).getTime() !== dateTo?.getTime()
    ) {
      setDateFrom(new Date(from));
      setDateTo(new Date(to));
    }
    if (!allSteps) {
      getStepsCount();
    }
  };

  const getPageDates = (pageNumber: number): Array<string> => {
    if (allSteps) {
      const { from, to } = calculatePageDates(pageNumber);
      return allSteps.slice(from, to + 1).map(e => e.toISOString());
    }
    return [];
  };

  const calculatePageDates = (pageNumber: number) => {
    const stepsArr = allSteps || [];
    const itemsPerPage = rows;
    const from = pageNumber === -1 ? 0 : pageNumber * itemsPerPage;
    const to =
      pageNumber === -1
        ? stepsArr.length - 1
        : Math.min(from + itemsPerPage - 1, stepsArr.length - 1);
    return { from, to };
  };

  const generateDataExportCSVTransposed = async () => {
    const headerLine = [""];

    if (allSteps) {
      allSteps.forEach(step => {
        headerLine.push(formatTime(step));
      });
    }

    if (error) {
      easasAlert({ summary: "Failed to fetch data", detail: error.message, severity: "error" });
      return;
    }

    let bodyLine = "";
    if (dataset) {
      dataset.forEach(obj => {
        let newLine = `"${obj.title}, ${obj.unit}",`;
        const dp = obj.decimalPlaces || 2;
        const dataMap = new Map(
          obj.data.map(item => [formatToIsoStringWithoutMillis(new Date(item.key)), item.value])
        );

        newLine += (allSteps || [])
          .map(step => {
            const stepKey = formatToIsoStringWithoutMillis(new Date(step));
            return dataMap.has(stepKey) ? Number(dataMap.get(stepKey)).toFixed(dp) : "-";
          })
          .join(",");

        newLine += "\n";
        bodyLine += newLine;
      });
    }

    const csvContent = `${headerLine.join(",")}\n${bodyLine}`;
    downloadBlobAsCsvFile(new Blob([csvContent]), "datasetTable.csv");
  };

  const generateDataExportCSV = async () => {
    let headerLine = "Date";
    datasetsArray.forEach(ob => {
      if (ob.unit && ob.unit !== " ") {
        if (ob.overriddenTitle) {
          headerLine = `${headerLine},"${ob.overriddenTitle}, ${ob.unit}"`;
        } else if (ob.title) {
          headerLine = `${headerLine},"${ob.title}, ${ob.unit}"`;
        } else {
          headerLine = `${headerLine},"${ob.identifier}, ${ob.unit}"`;
        }
      } else if (ob.overriddenTitle) {
        headerLine = `${headerLine},${ob.overriddenTitle}`;
      } else if (ob.title) {
        headerLine = `${headerLine},${ob.title}`;
      } else {
        headerLine = `${headerLine},${ob.identifier}`;
      }
    });

    if (error) {
      easasAlert({ summary: "Failed to fetch data", detail: error.message, severity: "error" });
      return "";
    }

    let bodyLine = "";
    if (mappedData) {
      mappedData.forEach(obj => {
        let newLine = "";
        Object.entries(obj).forEach(([key, value]) => {
          if (key === "date") {
            newLine = dateBody(value as string, dateFormat) + newLine;
          } else {
            newLine += ",";
            newLine += value && value !== " " ? value : "-";
          }
        });
        newLine += "\n";
        bodyLine += newLine;
      });
    }

    const csvContent = `${headerLine}\n${bodyLine}`;
    return csvContent;
  };

  const handleDataExport = async () => {
    setRequesting(true);

    try {
      let csvData: string | void;
      if (config?.transposed) {
        csvData = await generateDataExportCSVTransposed();
      } else {
        csvData = await generateDataExportCSV();
      }

      if (csvData) {
        const blob = new Blob([csvData], { type: "text/csv" });
        downloadBlobAsCsvFile(blob, "datasetTable.csv");
      }
    } catch (err) {
      easasAlert({ summary: "Error occurred during data export", severity: "error" });
    } finally {
      setRequesting(false);
    }
  };

  const getAllDates = (currentData: Array<any>): Array<any> => {
    let allTimestamps: Array<any> = [];
    const tempColumnObj: any = {};

    currentData.forEach(o => {
      tempColumnObj[o.identifier] = "";
    });

    if (dateTo && allSteps) {
      if (config?.sort === "DESC" && dateTo.getTime() !== allSteps[0].getTime()) {
        getStepsCount();
      }
      if (config?.sort === "ASC" && dateTo.getTime() !== allSteps[allSteps.length - 1].getTime()) {
        getStepsCount();
      }
    }

    if (allSteps) {
      allTimestamps = allSteps.map(obj => ({
        ...tempColumnObj,
        date: `${obj.toISOString().split(".")[0]}Z`,
      }));
    }

    return allTimestamps;
  };

  const mapAndReplaceDateProperties = (
    dates: Array<any>,
    currentData: Array<DatasetDataInfoResponse>
  ) => {
    return dates.map(obj => {
      const tempObj = { ...obj };
      Object.keys(obj).forEach(ob => {
        const objTime = new Date(obj.date).getTime();
        if (ob !== "date") {
          const temp = currentData.find(res => res.identifier === ob)?.data || [];
          if (temp.length > 0) {
            const ansValue = findValue(temp, objTime);
            if (ansValue || ansValue === 0) {
              tempObj[ob] = ansValue;
            }
          }
        }
      });
      return tempObj;
    });
  };

  const mapAndTransformDataResponse = (
    dates: Array<string>,
    dataRes: Array<DatasetDataInfoResponse>
  ) => {
    return dataRes.map(res => {
      const conf = datasets?.find(e => e.identifier === res.identifier);

      const tempObj: any = {
        title: res.title,
        unit: res.unit,
        identifier: res.identifier,
        decimalPlaces: conf?.decimalPlaces || res.decimalPlaces,
        overriddenTitle: conf?.overriddenTitle,
      };

      const decimalPlaces = res.decimalPlaces || 2;

      res.data.forEach(d => {
        const date = new Date(d.key);
        date.setMilliseconds(0);
        const tempKey = date.toISOString();

        if (dates.includes(tempKey)) {
          tempObj[tempKey] = d.value.toFixed(decimalPlaces);
        }
      });

      return tempObj;
    });
  };

  const getTimeRange = () => {
    const validatedConfig = config ? timeConfigValidation(config) : timeConfigValidation({});

    const startingDate = referenceTime ? new Date(referenceTime) : new Date();
    const tempDate = truncateDate(startingDate, config?.alignStartTo);

    const tempFrom = addToDate(
      tempDate,
      validatedConfig.startOffset,
      validatedConfig.startOffsetUnits
    );
    const tempTo = addToDate(tempFrom, validatedConfig.leadOffset, validatedConfig.leadUnits);

    const from = addToDate(tempFrom, validatedConfig.lagOffset * -1, validatedConfig.lagUnits);
    return compareTwoDates(from, tempTo);
  };

  const setCellStyle = (
    identifier: string,
    value: number | undefined
  ): CSSProperties | undefined => {
    const id = mappedHighlights.find(e => e.identifier === identifier);

    const [color, bgColor] = getValueColors(value, {
      highlightRanges: id?.highlightRanges,
      defaultHighlightColor: id?.defaultHighlight,
    });

    return { color, backgroundColor: bgColor };
  };

  const calculateTotals = (currentData: any[], dsArray: any[]) => {
    const calculateTotal = (currentDataset: any) => {
      if (!currentDataset.identifier) {
        return null;
      }

      let tempTotal = 0;
      let totalActualValues = 0;
      currentData.map(element => {
        const value = element[currentDataset.identifier];
        if (typeof value === "number") {
          tempTotal += value;
          totalActualValues++;
        }
        return null;
      });
      if (totalActualValues > 0) {
        let average;

        switch (currentDataset.totalType || config?.totalType) {
          case "SUM":
            return {
              identifier: currentDataset.identifier,
              value: tempTotal.toFixed(currentDataset.decimalPlaces),
              tooltip: "Sum",
            };
          case "AVG":
            average = tempTotal / totalActualValues;
            return {
              identifier: currentDataset.identifier,
              value: average.toFixed(currentDataset.decimalPlaces),
              tooltip: "Average",
            };
          default:
            return { identifier: currentDataset.identifier };
        }
      } else {
        return { identifier: currentDataset.identifier };
      }
    };

    const calculateTotalsRecursive = (ds: any) => {
      if (ds.children) {
        return ds.children.flatMap(calculateTotalsRecursive);
      }
      return calculateTotal(ds);
    };

    const allTotals = dsArray.flatMap(calculateTotalsRecursive).filter(total => total !== null);

    setTotals(allTotals);
  };

  const flattenDatasets = (dsArray: any[]) => {
    const currentDatasets: IFlattenedDataset[] = [];
    const extractDatasetData = (obj: any, parentHeaders: string = "") => {
      const currentHeaders = obj.header
        ? parentHeaders && `${parentHeaders}: ${obj.header}`
        : parentHeaders;
      if (obj.children) {
        obj.children.forEach((child: any) => extractDatasetData(child, currentHeaders));
      } else {
        const finalTitle = currentHeaders
          ? `${currentHeaders}: ${obj.overriddenTitle || obj.title}`
          : `${obj.overriddenTitle || obj.title}`;
        currentDatasets.push({
          title: finalTitle,
          identifier: obj.identifier,
          unit: obj.unit,
          decimalPlaces: obj.decimalPlaces,
        });
      }
    };
    dsArray.forEach(ds => extractDatasetData(ds));
    return currentDatasets;
  };

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    const currentDatasets = ["Date", ...(flattenDatasets(datasetsArray) || [])];
    const rowData = getRowData(mappedData, currentDatasets, dateFormat);

    const headersTitles = currentDatasets.map(ds => {
      if (typeof ds === "string") {
        return "Date";
      }
      if (ds.unit && ds.unit !== " ") {
        return `${ds.title}, ${ds.unit}`;
      }
      return ds.title;
    });

    const WS_DATA = [
      headersTitles,
      ...rowData.map(row =>
        currentDatasets.map(header =>
          typeof header === "string" ? row.Date : row[header.identifier]
        )
      ),
    ];

    const ws = XLSX.utils.aoa_to_sheet(WS_DATA);

    XLSX.utils.book_append_sheet(wb, ws, "Data");

    XLSX.writeFile(wb, "data.xlsx");
  };

  const mappedData = useMemo(() => {
    if (dataset) {
      return config?.transposed ? mapDataTransposed(dataset) : mapData(dataset);
    }
    return [];
  }, [isLoading, dataset, allSteps]);

  const datasetsArray = useMemo(() => {
    if (!dataset) {
      return datasets || [];
    }
    return updateDatasetsArrayWithInfo(dataset);
  }, [isLoading, dataset, allSteps]);

  useEffect(() => {
    let customData = mappedData;
    if (config?.showEmpty === false) {
      customData = mappedData.filter(obj => {
        const tempObj = structuredClone(obj);
        delete tempObj.date;
        const allIsEmpty = Object.values(tempObj).every(v => v === "");
        return !allIsEmpty;
      });
    }

    setFilteredSteps(customData.length);
    setFilteredData(customData.slice(page, page + rows));

    if (config?.totalSize === "ALL") {
      calculateTotals(customData, datasetsArray as DatasetDefaultConfig[]);
    } else {
      calculateTotals(customData.slice(page, page + rows), datasetsArray as DatasetDefaultConfig[]);
    }
  }, [mappedData, currentPage, rows]);

  const handleSelectTable = () => {
    if (config?.transposed) {
      return (
        <DatasetTableTransposed
          config={config}
          reportOptions={reportOptions}
          reportMode={reportMode}
          data={filteredData}
          getPageDates={getPageDates}
          currentPage={currentPage}
          highlightRanges={mappedHighlights}
          formatNumbers={formatNumbers}
        />
      );
    }
    if (tableMode === DatasetTableMode.GROUPED) {
      return (
        <DatasetTableGrouped
          config={config}
          datasetsArray={datasetsArray}
          data={filteredData}
          totals={totals}
          setIsDialogOpen={setIsDialogOpen}
          formatNumbers={formatNumbers}
        />
      );
    }
    return (
      <DatasetTableDefault
        config={config}
        datasetsArray={datasetsArray}
        data={filteredData}
        getCellStyle={setCellStyle}
        totals={totals}
        setIsDialogOpen={setIsDialogOpen}
        dateFrom={dateFrom}
        dateTo={dateTo}
        formatNumbers={formatNumbers}
      />
    );
  };

  const renderTable = () => {
    if (isLoading) {
      return (
        <div className="loading-screen">
          <ProgressSpinner style={{ overflow: "hidden" }} />
        </div>
      );
    }

    if (errorMessage) {
      return <div className="font-bold text-red-500">{errorMessage}</div>;
    }
    return (
      <div className="table-with-paginator">
        <div id={tableId}>{handleSelectTable()}</div>
        <Paginator
          first={page}
          className="non-printable"
          rows={rows}
          onPageChange={e => {
            if (requesting) {
              return;
            }

            setCurrentPage(e.page);
            setPage(e.first);
            setRows(e.rows);
          }}
          totalRecords={filteredSteps || 0}
          template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
          rowsPerPageOptions={rowsPerPageOptions}
        />
      </div>
    );
  };

  return (
    <div className="dataset-table-bull-window" id={uniqueComponentId}>
      <div className="config-container non-printable">
        <div className="date-inputs-container">
          <div className="calendar-container">
            <b style={{ padding: "0 0 0 5px" }}>From</b>
            <CalendarWrapper
              value={dateFrom}
              onChange={e => {
                setRefresh(false);
                setDateFrom(e);
              }}
              dateFormat="yy-mm-dd"
            />
          </div>
          <div className="calendar-container">
            <b style={{ padding: "0 0 0 5px" }}>To</b>
            <CalendarWrapper
              value={dateTo}
              onChange={e => {
                setRefresh(false);
                setDateTo(e);
              }}
              dateFormat="yy-mm-dd"
            />
          </div>
          <div>
            <Button
              icon="pi pi-file-edit"
              onClick={() => {
                setFormatNumbers(!formatNumbers);
              }}
              tooltip="Format numbers"
              loading={requesting}
            />
          </div>
        </div>
        <div className="button-container">
          {!refresh && (
            <Button
              icon="pi pi-history"
              onClick={() => {
                setRefresh(true);
                if (currentPage !== 0) {
                  setCurrentPage(0);
                }
                if (page !== 1) {
                  setPage(1);
                }
                refetch();
              }}
              loading={requesting}
              disabled={refresh}
            />
          )}
          <div className="button-to-left">
            {tableMode === DatasetTableMode.SINGLE ? (
              <Button
                icon="pi pi-file"
                onClick={() => handleDataExport()}
                tooltip="Export"
                loading={requesting}
              />
            ) : (
              <Button
                type="button"
                icon="pi pi-file-excel"
                onClick={exportExcel}
                className="p-button-success mr-2"
                data-pr-tooltip="XLS"
              />
            )}
            <Button
              icon="pi pi-refresh"
              onClick={() => truncateStarterDateFromConfig()}
              loading={requesting}
            />
          </div>
        </div>
      </div>
      {renderTable()}
    </div>
  );
};

export default DatasetTable;
